import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Alert from "@shared/v2/Alert";
import Toast from "@shared/Toast";
import { TriangleExclamationSolidV6 } from "@shared/v2/Icomoon";
import Spinner from "@icons/Spinner";
import {
  AddEditAddressModal,
  AddressModalCallbackProvider,
  useAddressModal,
  useAddressModalCallback,
} from "@shared/AddressModal";
import InvalidAddressModal from "../PersonDetail/components/InvalidAddressModal";
import VerifyAddressModal from "../PersonDetail/components/VerifyAddressModal";
import * as api from "./actions/api";
import BrandingCard from "./components/BrandingCard";
import OfficeCards from "./components/OfficeCards";
import ReviewCard from "./components/ReviewCard";
import SocialCard from "./components/SocialCard";
import TeamCard from "./components/TeamCard";

const TOAST_TEXT_INIT = "Social links did not save successfully.";

const Main = () => {
  const dispatch = useDispatch();
  const hasError = useSelector((state) => state.businessSettings.hasError);
  const isLoading = useSelector((state) => state.businessSettings.isLoading);
  const model = useSelector((state) => state.businessSettings.model);
  useEffect(() => {
    dispatch(api.loadBusinessSettings());
  }, []);
  const [customToast, setCustomToast] = useState({
    text: TOAST_TEXT_INIT,
    show: false,
  });

  useEffect(() => {
    if (customToast.show) {
      setTimeout(() => {
        setCustomToast((prev) => ({ ...prev, show: false }));
      }, 5000);
    }
  }, [customToast.show]);

  if (isLoading) {
    return (
      <div className="tw-pt-[90px] tw-text-center">
        <Spinner size="xxxl" className="fa-spin" />
      </div>
    );
  }

  return (
    <AddressModalCallbackProvider>
      <BusinessSettingsAddressModal />
      <BusinessSettingsInvalidAddressModal />
      <BusinessSettingsVerifyAddressModal />
      <div className="tw-p-[16px] tw-bg-tinted-gray-50 tw-flex tw-flex-wrap tw-gap-[16px] tw-max-w-[1200px] tw-mx-auto">
        {(hasError || !model) && (
          <Alert
            containerClass="tw-w-full"
            variant="error"
            title="Something went wrong"
            text="Failed to load business settings. Try again later."
          />
        )}
        <Toast
          text={customToast.text}
          containerClassName={`${customToast.show ? "tw-mt-[-100px] tw-opacity-100" : "tw-mt-[-80px] tw-opacity-0"} tw-p-12px tw-z-[9991] tw-transform tw--translate-x-1/2 tw-transition-all tw-duration-500 tw-h-[40px] !tw-z-1000 !tw-bg-[#FAE6E6] !tw-border-[#FAC5C3]`}
          textClassName="!tw-text-[#A81E19]"
          iconClassName="tw-text-[#E5342E]"
          Icon={TriangleExclamationSolidV6}
        />
        {model && (
          <>
            <TeamCard />
            <BrandingCard />
            <OfficeCards />
            <SocialCard />
            <ReviewCard />
          </>
        )}
        <input
          type="text"
          className="tw-h-[1px] tw-w-[1px] tw-absolute tw-top-[-100%] tw-left-[-100%]"
          id="businessCopyInput"
        />
      </div>
    </AddressModalCallbackProvider>
  );
};

const BusinessSettingsAddressModal = () => {
  const addressModal = useAddressModal("addEdit");
  const { getOnSubmit } = useAddressModalCallback();

  return (
    <AddEditAddressModal
      initAddress={addressModal.modalState.initAddress}
      show={addressModal.isOpen}
      onClose={addressModal.close}
      onSubmit={getOnSubmit()}
      title={addressModal.modalState.title}
      onSaveTxt="Save"
      hideAddressType={addressModal.modalState.hideAddressType}
      showArmedForcesOptions={false}
    />
  );
};

const BusinessSettingsInvalidAddressModal = () => {
  const invalidModal = useAddressModal("invalid");
  const { getInvalidOnSubmit, getInvalidOnEdit } = useAddressModalCallback();

  return (
    <InvalidAddressModal
      address={invalidModal.modalState.address}
      show={invalidModal.isOpen}
      onClose={invalidModal.close}
      onSubmit={getInvalidOnSubmit()}
      onEdit={getInvalidOnEdit()}
    />
  );
};

const BusinessSettingsVerifyAddressModal = () => {
  const verifyModal = useAddressModal("verify");
  const { getVerifyOnSubmit, getVerifyOnEdit } = useAddressModalCallback();

  return (
    <VerifyAddressModal
      originalAddress={verifyModal.modalState.originalAddress}
      suggestedAddress={verifyModal.modalState.suggestedAddress}
      show={verifyModal.isOpen}
      onClose={verifyModal.close}
      onSubmit={getVerifyOnSubmit()}
      onEdit={getVerifyOnEdit()}
    />
  );
};

export default Main;
